import { icon, hasIcon } from '../icons.js'
import { escapeHtml } from '../util.js'
import { isFeatureAvailable } from '../store.js'

function tile(href, iconName, label, note, cls = '') {
  return `
    <a class="home-tile ${cls}" href="${href}">
      <span class="home-tile-icon">${icon(iconName)}</span>
      <span class="home-tile-label">${label}</span>
      ${note ? `<span class="home-tile-note">${note}</span>` : ''}
    </a>`
}

export function renderHome(root, ctx) {
  const nodeCount = ctx.data.nodes?.length ?? 0
  const hikeCount = ctx.data.hikes?.length ?? 0
  const quizCount = ctx.data.quiz?.length ?? 0
  const bingoCount = ctx.data.bingo?.length ?? 0
  const region = ctx.region
  const langAdj = ctx.data.meta?.language ? ctx.data.meta.language + 'e' : 'Lokale'

  const heroImg = region?.heroImage
  const heroHtml = heroImg
    ? `<div class="home-hero"><img class="home-hero-img" src="${heroImg}" alt="${escapeHtml(region?.label ?? 'Reisgids')}" /></div>`
    : `<div class="home-hero home-hero-text"><h1 class="home-hero-title">${escapeHtml(region?.label ?? 'Reisgids')}</h1></div>`

  // Ontdek-tiles: altijd kaart, rest feature-afhankelijk
  const discoverTiles = []
  discoverTiles.push(tile('#/map', 'map', 'Kaart', `${nodeCount} plekken`))
  if (isFeatureAvailable('guides', 'guides'))
    discoverTiles.push(tile('#/highlights', 'compass', 'Highlights', 'Onze toplijstjes'))
  if (isFeatureAvailable('hikes', 'hikes'))
    discoverTiles.push(tile('#/hikes', 'footprints', 'Wandelen', hikeCount ? `${hikeCount} routes` : 'Routes'))
  if (isFeatureAvailable('compendium', 'compendium'))
    discoverTiles.push(tile('#/encyclopedie', 'book-open', 'Encyclopedie', 'Verhalen & context'))
  if (isFeatureAvailable('phrases', 'phrases'))
    discoverTiles.push(tile('#/phrases', 'message', `${langAdj} taal`, `${ctx.data.phrases?.length ?? 0} zinnen`))

  // Doe-mee sectie
  const doeMeeTiles = []
  if (isFeatureAvailable('bingo', 'bingo'))
    doeMeeTiles.push(tile('#/bingo', 'grid', 'Bingo', `${bingoCount} uitdagingen`, 'accent'))
  if (isFeatureAvailable('quiz', 'quiz'))
    doeMeeTiles.push(tile('#/quiz', 'sparkles', 'Quiz', `${quizCount} vragen`, 'accent'))

  // Handig-items
  const handigItems = []
  if (isFeatureAvailable('people', 'people')) {
    handigItems.push(`<a class="home-list-item" href="#/people">${icon('users')} Beroemde figuren</a>`)
    handigItems.push(`<a class="home-list-item" href="#/nearest-celeb">${icon('navigation')} Dichtstbijzijnde beroemdheid</a>`)
  }

  root.innerHTML = `
    <div class="home">
      ${heroHtml}

      <div class="home-body">
        <div class="home-search">
          <div class="home-search-input-wrap">
            ${icon('compass')}
            <input type="search" id="home-search" class="home-search-input" placeholder="Zoek plekken, verhalen, personen…" autocomplete="off" />
          </div>
          <div id="home-search-results" class="home-search-results" hidden></div>
        </div>

        <section class="home-section">
          <h2 class="home-section-title">Ontdek</h2>
          <div class="home-grid main">${discoverTiles.join('')}</div>
        </section>

        <section class="home-section">
          <div class="home-cta" id="home-nearby-btn">
            <span class="home-cta-icon">${icon('navigation')}</span>
            <span class="home-cta-text">
              <span class="home-cta-title">Wat is er leuk in de buurt?!</span>
              <span class="home-cta-note">3 verrassende suggesties bij jou om de hoek</span>
            </span>
            <span class="home-cta-arrow">${icon('chevron-right')}</span>
          </div>
        </section>

        ${doeMeeTiles.length ? `
        <section class="home-section">
          <h2 class="home-section-title">Doe mee</h2>
          <div class="home-grid compact">${doeMeeTiles.join('')}</div>
        </section>` : ''}

        ${handigItems.length ? `
        <section class="home-section">
          <h2 class="home-section-title">Handig</h2>
          <div class="home-list">${handigItems.join('')}</div>
        </section>` : ''}
      </div>
    </div>`

  root.querySelector('#home-nearby-btn')?.addEventListener('click', () => {
    location.hash = '#/nearby'
  })

  const searchInput = root.querySelector('#home-search')
  const searchResults = root.querySelector('#home-search-results')
  if (searchInput && searchResults) {
    const nodes = ctx.data.nodes ?? []
    const people = ctx.data.people ?? []
    const compendium = ctx.data.compendium ?? []

    searchInput.addEventListener('input', () => {
      const q = searchInput.value.trim().toLowerCase()
      if (q.length < 2) { searchResults.hidden = true; return }

      const hits = []
      for (const n of nodes) {
        if (hits.length >= 8) break
        if (n.name.toLowerCase().includes(q) || n.blurb?.toLowerCase().includes(q)) {
          const cat = ctx.indexes.categoryById.get(n.category)
          const catIcon = cat && hasIcon(cat.icon) ? icon(cat.icon) : icon('map')
          hits.push({ href: `#/node/${n.id}`, icon: catIcon, title: n.name, sub: cat?.label ?? '', catClass: `cat-${n.category}` })
        }
      }
      if (isFeatureAvailable('people', 'people')) {
        for (const p of people) {
          if (hits.length >= 10) break
          if (p.name.toLowerCase().includes(q)) {
            hits.push({ href: `#/people/${p.id}`, icon: icon('users'), title: p.name, sub: p.years ?? '' })
          }
        }
      }
      for (const c of compendium) {
        if (hits.length >= 12) break
        if (c.title.toLowerCase().includes(q) || c.summary?.toLowerCase().includes(q)) {
          hits.push({ href: `#/encyclopedie/${c.id}`, icon: icon('book-open'), title: c.title, sub: c.kind ?? '' })
        }
      }

      if (!hits.length) {
        searchResults.innerHTML = `<div class="home-search-empty">Geen resultaten voor "${escapeHtml(q)}"</div>`
        searchResults.hidden = false
        return
      }

      searchResults.innerHTML = hits.map(h => `
        <a class="home-search-hit ${h.catClass || ''}" href="${h.href}">
          <span class="home-search-hit-icon">${h.icon}</span>
          <span class="home-search-hit-text">
            <span class="home-search-hit-title">${escapeHtml(h.title)}</span>
            ${h.sub ? `<span class="home-search-hit-sub">${escapeHtml(h.sub)}</span>` : ''}
          </span>
        </a>`).join('')
      searchResults.hidden = false
    })

    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { searchInput.value = ''; searchResults.hidden = true }
    })
  }
}
